import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CaretLeft, CaretRight, CookingPot, FilePdf } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
const DAYS = ['segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado', 'domingo'];
const DAY_LABELS = { segunda: 'Segunda', terca: 'Terça', quarta: 'Quarta', quinta: 'Quinta', sexta: 'Sexta', sabado: 'Sábado', domingo: 'Domingo' };
const MEAL_LABELS = { sopa_almoco: 'Sopa (almoço)', almoco: 'Almoço', sopa_jantar: 'Sopa (jantar)', jantar: 'Jantar' };
const EXTRA_SECTION_LABELS = { sopas: 'Sopas', acompanhamentos: 'Acompanhamentos', snacks: 'Snacks', jantares_rapidos: 'Jantares rápidos' };

/** Vista read-only (cliente) da Ementa Fixa do Programa: 4 semanas paginadas + secções extra + nota. */
export default function ProgramMenuView({ programMenu }) {
  const [weekIdx, setWeekIdx] = useState(0);
  if (!programMenu) return null;
  const week = programMenu.weeks?.[weekIdx];
  const recipeMap = programMenu.recipe_map || {};
  const getName = (id) => id ? (recipeMap[id] || 'Receita') : null;

  const downloadPdf = async () => {
    try {
      const r = await axios.get(`${API}/api/program-menus/${programMenu.patient_type}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `ementa_fixa_${programMenu.patient_type}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Erro ao gerar PDF'); }
  };

  return (
    <div data-testid="program-menu-view">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs bg-brand-gold/15 text-brand-gold-hover px-2.5 py-1 rounded-full font-medium">{programMenu.program_label}</span>
        <button onClick={downloadPdf} className="flex items-center gap-1.5 text-xs text-brand-green hover:text-brand-green-hover" data-testid="client-download-pdf-btn">
          <FilePdf size={15} /> PDF
        </button>
      </div>
      <div className="flex items-center justify-between mb-3">
        <span />
        <div className="flex items-center gap-2">
          <button disabled={weekIdx === 0} onClick={() => setWeekIdx(w => Math.max(0, w - 1))} className="p-1.5 rounded-full hover:bg-brand-mint/20 disabled:opacity-30 transition-colors" data-testid="program-week-prev">
            <CaretLeft size={16} className="text-brand-green" />
          </button>
          <span className="text-sm font-medium text-brand-text" data-testid="program-week-label">Semana {weekIdx + 1} de 4</span>
          <button disabled={weekIdx === 3} onClick={() => setWeekIdx(w => Math.min(3, w + 1))} className="p-1.5 rounded-full hover:bg-brand-mint/20 disabled:opacity-30 transition-colors" data-testid="program-week-next">
            <CaretRight size={16} className="text-brand-green" />
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {DAYS.map(day => {
          const dayMeals = week?.days?.[day] || {};
          const hasAny = Object.values(dayMeals).some(Boolean);
          if (!hasAny) return null;
          return (
            <div key={day} className="bg-white border border-brand-border rounded-3xl p-4 shadow-[0_8px_32px_rgba(101,113,97,0.04)]" data-testid={`program-day-${day}`}>
              <h3 className="font-heading font-medium text-brand-text mb-3">{DAY_LABELS[day]}</h3>
              <div className="grid grid-cols-2 gap-3">
                {['sopa_almoco', 'almoco', 'sopa_jantar', 'jantar'].map(mealType => {
                  const name = getName(dayMeals[mealType]);
                  if (!name) return null;
                  return (
                    <div key={mealType}>
                      <p className="text-xs text-brand-muted mb-1.5 uppercase tracking-wider">{MEAL_LABELS[mealType]}</p>
                      <div className="bg-brand-mint/15 border border-brand-mint/30 rounded-2xl px-3 py-2.5 text-sm text-brand-text" data-testid={`program-meal-${day}-${mealType}`}>
                        <CookingPot size={14} className="inline mr-1.5 text-brand-green" weight="duotone" />
                        {name}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {Object.entries(EXTRA_SECTION_LABELS).map(([key, label]) => {
        const ids = programMenu.extra_sections?.[key] || [];
        if (ids.length === 0) return null;
        return (
          <div key={key} className="mt-4" data-testid={`program-extra-${key}`}>
            <h4 className="text-xs uppercase tracking-wider text-brand-muted mb-2">{label}</h4>
            <div className="flex flex-wrap gap-2">
              {ids.map(id => (
                <span key={id} className="bg-brand-bg text-brand-text text-sm px-3 py-1.5 rounded-full">{getName(id)}</span>
              ))}
            </div>
          </div>
        );
      })}

      {programMenu.note?.trim() && (
        <div className="mt-5 bg-brand-gold/10 border border-brand-gold/20 rounded-2xl p-4" data-testid="program-note">
          <p className="text-sm text-brand-text whitespace-pre-wrap">{programMenu.note}</p>
        </div>
      )}
    </div>
  );
}
